/**
 * LCFPIInvestmentCommitteeMode.js
 * THE primary and only LCFPI platform experience.
 * Bloomberg Terminal × Climate Finance Intelligence.
 * DeCarbonIQ branding. Role-based actions. Map-first tab order.
 */
import React, { useState, useEffect, useCallback, Suspense, lazy, useRef } from "react";
import client from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import {
  Download, Loader, ChevronLeft, ChevronRight, ChevronDown, RefreshCw,
  Globe, DollarSign, TrendingUp, GitMerge, Shield, PlusCircle,
  Leaf, BarChart2, CheckCircle2, Clock, X, FileText, Share2,
  Search, AlertCircle, Eye, Target, Upload,
} from "lucide-react";
import LCFPIGeoMap from "@/components/lcfpi/LCFPIGeoMap";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { NewProjectModal, ProjectWorkspaceModal } from "./LCFPIProjectModals";
import { MonetisationDrilldownModal, PoACarbonWorkspace } from "./LCFPIMonetisation";
import { DFIMatchingModal } from "./LCFPIDFIMatching";
import { BulkUploadWizard } from "./LCFPIBulkUpload";
import {
  UserMenuDropdown, LockScreen, InactivityWarning, useInactivityTimer,
} from "./LCFPISecurityModals";

// DeCarbonIQ logo — same asset used across the entire platform
const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

const LCFPIAuditTrail     = lazy(() => import("./LCFPIAuditTrail"));
const LCFPIPublicRegistry = lazy(() => import("./LCFPIPublicRegistry"));
const LCFPIReports        = lazy(() => import("./LCFPIReports"));

// ── Utils ────────────────────────────────────────────────────────────────────
const fmtU = v => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e9) return `$${(n/1e9).toFixed(1)}B`; if (n >= 1e6) return `$${(n/1e6).toFixed(1)}M`; if (n >= 1e3) return `$${(n/1e3).toFixed(0)}K`; return `$${n}`; };
const fmtN = (v, u = "") => { if (!v && v !== 0) return "—"; const n = Number(v); if (n >= 1e6) return `${(n/1e6).toFixed(1)}M ${u}`.trim(); if (n >= 1e3) return `${(n/1e3).toFixed(0)}K ${u}`.trim(); return `${n.toLocaleString()} ${u}`.trim(); };
const TIER_COLOR = { "Tier 1": "#22c55e", "Tier 2": "#f59e0b", "Tier 3": "#ef4444" };
const TIER_BG    = { "Tier 1": "#14532d40", "Tier 2": "#78350f40", "Tier 3": "#7f1d1d40" };

// ── Role detection ───────────────────────────────────────────────────────────
const OPERATIONAL_ROLES = ["admin", "lcfpi", "government", "programme_manager", "staff", "manager"];
const isOperational = (role) => OPERATIONAL_ROLES.some(r => (role || "").toLowerCase().includes(r));

// ── Tab definitions (Map is #2, Climate renamed to Impact) ──────────────────
const TABS = [
  { id: "live",       label: "Live Intelligence",       icon: BarChart2,  accent: "#3b82f6" },
  { id: "map",        label: "Investment Map",          icon: Globe,      accent: "#0891b2" },
  { id: "portfolio",  label: "Portfolio",               icon: GitMerge,   accent: "#22c55e" },
  { id: "opportunity",label: "Opportunities",           icon: DollarSign, accent: "#a78bfa" },
  { id: "poa",        label: "Programme of Activities", icon: TrendingUp, accent: "#f59e0b" },
  { id: "climate",    label: "Impact",                  icon: Leaf,       accent: "#34d399" },
  { id: "governance", label: "Governance",              icon: Shield,     accent: "#94a3b8" },
];

// ── SDG catalogue ────────────────────────────────────────────────────────────
const SDG_META = {
  1:  { label: "No Poverty",           color: "#e5243b" },
  2:  { label: "Zero Hunger",          color: "#dda63a" },
  3:  { label: "Good Health",          color: "#4c9f38" },
  4:  { label: "Quality Education",    color: "#c5192d" },
  5:  { label: "Gender Equality",      color: "#ff3a21" },
  6:  { label: "Clean Water",          color: "#26bde2" },
  7:  { label: "Clean Energy",         color: "#fcc30b" },
  8:  { label: "Decent Work",          color: "#a21942" },
  9:  { label: "Industry",             color: "#fd6925" },
  10: { label: "Reduced Inequalities", color: "#dd1367" },
  11: { label: "Sustainable Cities",   color: "#fd9d24" },
  12: { label: "Responsible Cons.",    color: "#bf8b2e" },
  13: { label: "Climate Action",       color: "#3f7e44" },
  14: { label: "Life Below Water",     color: "#0a97d9" },
  15: { label: "Life on Land",         color: "#56c02b" },
  17: { label: "Partnerships",         color: "#19486a" },
};

// ── Shared components ────────────────────────────────────────────────────────
function KPI({ label, value, color = "#60a5fa", sub }) {
  return (
    <div data-testid={`ic-kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}
      className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
      <p className="text-2xl font-black mb-0.5" style={{ color }}>{value ?? "—"}</p>
      <p className="text-xs font-semibold text-slate-300">{label}</p>
      {sub && <p className="text-[10px] text-slate-600 mt-0.5">{sub}</p>}
    </div>
  );
}

function TierBadge({ tier }) {
  const c = TIER_COLOR[tier] || "#64748b";
  return <span style={{ background: TIER_BG[tier]||"#1e293b40", color: c, border:`1px solid ${c}30` }}
    className="text-[10px] font-bold px-2 py-0.5 rounded-full">{tier||"—"}</span>;
}

function ProjectCard({ p, onOpen }) {
  const tc = TIER_COLOR[p.readiness_tier] || "#64748b";
  const trustColor = {
    "Registry Ready": "#22c55e", "Verified": "#0891b2", "Validated": "#3b82f6",
    "Programme Reviewed": "#8b5cf6", "Screened": "#f59e0b", "Indicative": "#64748b",
  }[p.trust_level] || "#64748b";
  return (
    <div data-testid={`lcfpi-project-card-${p.project_id}`}
      onClick={() => onOpen(p.project_id)}
      style={{ borderLeft: `3px solid ${tc}`, cursor: "pointer" }}
      className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 flex items-center gap-4 hover:bg-slate-800 transition-all group">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-sm font-bold text-white truncate">{p.org_name||p.project_name||"—"}</p>
          <TierBadge tier={p.readiness_tier} />
        </div>
        <p className="text-xs text-slate-500 truncate">{p.sector?.replace(/_/g," ")} · {p.lga||"—"}</p>
      </div>
      <div className="flex items-center gap-5 flex-shrink-0">
        <div className="text-right"><p className="text-sm font-black text-blue-400">{fmtU(p.funding_need_usd)}</p><p className="text-[10px] text-slate-500">Need</p></div>
        <div className="text-center"><p className="text-sm font-black" style={{color:tc}}>{p.readiness_score?.toFixed(0)??"—"}</p><p className="text-[10px] text-slate-500">Readiness</p></div>
        <div className="text-center">
          <p className="text-xs font-bold" style={{color: trustColor}}>{p.trust_level || "Indicative"}</p>
          <p className="text-[10px] text-slate-500">Trust</p>
        </div>
      </div>
      <ChevronRight size={13} className="text-slate-600 group-hover:text-slate-400 flex-shrink-0 transition-colors" />
    </div>
  );
}

// ── Create PoA Modal ─────────────────────────────────────────────────────────
function CreatePoAModal({ projects, onClose, onCreated }) {
  const [form, setForm] = useState({ name:"", sector:"renewable_energy", methodology:"AMS-III.H", boundary:"Lagos State" });
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const eligible = (projects||[]).filter(p => (p.readiness_score||0) >= 40);
  const set = (k,v) => setForm(f => ({...f,[k]:v}));
  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x=>x!==id) : [...s,id]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Programme name required");
    if (selected.length === 0) return toast.error("Select at least one project");
    setSaving(true);
    try {
      await client.post("/lcfpi/poa/programmes", { ...form, project_ids: selected });
      toast.success("PoA programme created");
      onCreated?.();
      onClose();
    } catch { toast.error("Failed to create programme"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background:"#000000bb" }}
      onClick={onClose}>
      <div className="rounded-2xl border border-slate-700 w-full max-w-lg max-h-[80vh] flex flex-col"
        style={{ background:"#0d1726" }} onClick={e=>e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
          <p className="text-sm font-bold text-white">Create Programme of Activities</p>
          <button onClick={onClose}><X size={16} className="text-slate-500 hover:text-slate-300" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Programme Name *</label>
            <input value={form.name} onChange={e=>set("name",e.target.value)}
              placeholder="Lagos Clean Energy PoA Phase 1"
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-blue-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Sector</label>
              <select value={form.sector} onChange={e=>set("sector",e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none">
                {["renewable_energy","waste_management","agriculture","transport","housing"].map(s => (
                  <option key={s} value={s}>{s.replace(/_/g," ")}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Methodology</label>
              <select value={form.methodology} onChange={e=>set("methodology",e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none">
                {["AMS-III.H","AMS-I.D","AMS-III.F","VM0042","AMS-II.C"].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Programme Boundary</label>
            <input value={form.boundary} onChange={e=>set("boundary",e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none" />
          </div>
          <div>
            <label className="text-xs text-slate-400 mb-2 block">Eligible Projects ({selected.length} selected)</label>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {eligible.map(p => (
                <label key={p.project_id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800 cursor-pointer">
                  <input type="checkbox" checked={selected.includes(p.project_id)} onChange={()=>toggle(p.project_id)}
                    className="accent-blue-500" />
                  <span className="text-xs text-slate-300 flex-1 truncate">{p.org_name||p.project_name}</span>
                  <TierBadge tier={p.readiness_tier} />
                </label>
              ))}
              {eligible.length === 0 && <p className="text-xs text-slate-600 py-2 text-center">No eligible projects found</p>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 px-6 py-4 border-t border-slate-800">
          <button onClick={onClose} className="flex-1 text-xs text-slate-400 border border-slate-700 rounded-xl py-2 hover:bg-slate-800">Cancel</button>
          <button onClick={save} disabled={saving}
            className="flex-1 text-xs font-bold text-white rounded-xl py-2 disabled:opacity-50"
            style={{background:"#1d4ed8"}}>
            {saving ? "Creating…" : "Create PoA"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Governance panel ─────────────────────────────────────────────────────────
const GOV_TABS = [
  { id:"review", label:"Review Queue" },
  { id:"audit",  label:"Audit Trail" },
  { id:"registry",label:"Registry" },
  { id:"reports", label:"Reports" },
];

function GovernancePanel({ projects, kpis }) {
  const [govTab, setGovTab] = useState("review");
  const pending   = (projects||[]).filter(p => p.status==="pending_review"||p.review_status==="pending");
  const verified  = (projects||[]).filter(p => p.review_status==="approved");
  const avgTrust  = (projects||[]).reduce((a,p)=>a+(p.trust_score||0),0) / Math.max(1,(projects||[]).length);
  const evidencePct = kpis?.evidence_completeness || 0;

  return (
    <div>
      {/* Governance KPI cards */}
      <div className="grid grid-cols-6 gap-3 mb-6">
        <KPI label="Pending Review"    value={pending.length}                   color="#f59e0b" />
        <KPI label="Prog. Reviewed"    value={kpis?.programme_reviewed ?? 0}    color="#1e40af" />
        <KPI label="Verified"          value={verified.length}                  color="#22c55e" />
        <KPI label="Registry Ready"    value={kpis?.registry_ready ?? 0}        color="#0891b2" />
        <KPI label="Evidence Avg"      value={`${Math.round(evidencePct)}%`}    color="#8b5cf6" />
        <KPI label="Avg Trust Score"   value={avgTrust.toFixed(0)+"/100"}       color="#34d399" />
      </div>

      {/* Sub-tab bar */}
      <div className="flex gap-1 mb-4 bg-slate-800/40 rounded-xl p-1 w-fit">
        {GOV_TABS.map(t => (
          <button key={t.id} onClick={() => setGovTab(t.id)}
            data-testid={`lcfpi-gov-subtab-${t.id}`}
            className={`text-xs px-3 py-1.5 rounded-lg font-semibold transition-all ${govTab===t.id?"bg-slate-600 text-white":"text-slate-500 hover:text-slate-300"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {govTab === "review" && (
        <div className="space-y-2">
          {pending.length === 0 && (
            <div className="text-center py-10 text-slate-600">
              <CheckCircle2 size={24} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Review queue is clear</p>
            </div>
          )}
          {pending.slice(0,10).map(p => (
            <div key={p.project_id} className="flex items-center gap-4 bg-slate-800/40 border border-amber-900/20 rounded-xl px-4 py-3">
              <Clock size={13} className="text-amber-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white truncate">{p.org_name||p.project_name}</p>
                <p className="text-xs text-slate-500">{p.sector?.replace(/_/g," ")} · {p.lga}</p>
              </div>
              <TierBadge tier={p.readiness_tier} />
              <span className="text-xs text-amber-400 font-semibold flex-shrink-0">Pending</span>
            </div>
          ))}
        </div>
      )}
      {govTab === "audit" && (
        <Suspense fallback={<div className="flex justify-center py-10"><Loader size={16} className="animate-spin text-slate-500" /></div>}>
          <div style={{background:"#060d1a"}}><LCFPIAuditTrail /></div>
        </Suspense>
      )}
      {govTab === "registry" && (
        <Suspense fallback={<div className="flex justify-center py-10"><Loader size={16} className="animate-spin text-slate-500" /></div>}>
          <div style={{background:"#060d1a"}}><LCFPIPublicRegistry /></div>
        </Suspense>
      )}
      {govTab === "reports" && (
        <Suspense fallback={<div className="flex justify-center py-10"><Loader size={16} className="animate-spin text-slate-500" /></div>}>
          <div style={{background:"#060d1a"}}><LCFPIReports /></div>
        </Suspense>
      )}
    </div>
  );
}

// ── Main platform ─────────────────────────────────────────────────────────────
export default function LCFPIInvestmentCommitteeMode() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [investor,  setInvestor]  = useState(null);
  const [poa,       setPoa]       = useState(null);
  const [poaProgs,  setPoaProgs]  = useState([]);
  const [live,      setLive]      = useState(null);
  const [projects,  setProjects]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState("live");
  const [downloading, setDownloading] = useState(null);
  const [search,    setSearch]    = useState("");
  const [poaModal,  setPoaModal]  = useState(false);
  const [newProjModal, setNewProjModal] = useState(false);
  const [editProjId, setEditProjId]  = useState(null);
  // New modals — pre-deployment sprint
  const [monetModal,    setMonetModal]    = useState(false);
  const [dfiProject,    setDfiProject]    = useState(null);
  const [bulkUpload,    setBulkUpload]    = useState(false);
  const [newProjMenu,   setNewProjMenu]   = useState(false); // New Project dropdown
  const newProjMenuRef = useRef(null);
  // Session security
  const [isLocked,      setIsLocked]      = useState(false);
  const [showLockWarn,  setShowLockWarn]  = useState(false);
  // Key used to force map remount on tab switch (Problem 6 fix)
  const [mapKey,    setMapKey]    = useState(0);
  const navigate = useNavigate();

  // ── Inactivity timer ────────────────────────────────────────────────────────
  useInactivityTimer({
    role: user?.role,
    active: !isLocked,
    onLock: () => {
      client.post("/lcfpi/session-events", { action: "auto_lock", metadata: {} }).catch(() => {});
      setShowLockWarn(false);
      setIsLocked(true);
    },
    onWarn: () => setShowLockWarn(true),
    onClearWarn: () => setShowLockWarn(false),
  });

  const refreshLive = useCallback(async () => {
    try { const r = await client.get("/lcfpi/live-intelligence"); setLive(r.data); } catch {}
  }, []);

  const loadPoaProgs = useCallback(async () => {
    try { const r = await client.get("/lcfpi/poa/programmes"); setPoaProgs(r.data || []); } catch {}
  }, []);

  useEffect(() => {
    Promise.all([
      client.get("/lcfpi/dashboard"),
      client.get("/lcfpi/investor/portfolio"),
      client.get("/lcfpi/poa/economics"),
      client.get("/lcfpi/projects"),
      client.get("/lcfpi/live-intelligence"),
      client.get("/lcfpi/poa/programmes"),
    ]).then(([d, inv, poaR, proj, liveR, progsR]) => {
      setDashboard(d.data);
      setInvestor(inv.data);
      setPoa(poaR.data);
      setProjects(proj.data.projects || []);
      setLive(liveR.data);
      setPoaProgs(progsR.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
    const t = setInterval(refreshLive, 30000);
    return () => clearInterval(t);
  }, [refreshLive]);

  // Force map remount when switching to map tab (Problem 6 fix)
  useEffect(() => {
    if (tab === "map") setMapKey(k => k + 1);
  }, [tab]);

  // Close New Project dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (newProjMenuRef.current && !newProjMenuRef.current.contains(e.target)) {
        setNewProjMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const downloadMemo = async (projectId) => {
    setDownloading(projectId);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lcfpi/pdf/investment-memo/${projectId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("decarboniq_token")}` } });
      if (!r.ok) throw new Error();
      const blob = await r.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `LCFPI-InvMemo-${projectId}.pdf`; a.click();
      URL.revokeObjectURL(url); toast.success("Memo downloaded");
    } catch { toast.error("Download failed"); }
    finally { setDownloading(null); }
  };

  const openProject = (id) => setEditProjId(id);
  const openNewProject = () => setNewProjModal(true);

  if (loading) return (
    <div className="fixed inset-0 flex items-center justify-center" style={{ background: "#060c1a" }}>
      <div className="text-center"><Loader size={22} className="animate-spin text-blue-400 mx-auto mb-3" />
        <p className="text-xs text-slate-500">Loading pipeline intelligence…</p></div>
    </div>
  );

  const kpis   = dashboard?.kpis || {};
  const opps   = investor?.opportunities || [];
  const tier1  = projects.filter(p => p.readiness_tier === "Tier 1");
  const tier2  = projects.filter(p => p.readiness_tier === "Tier 2");
  const tier3  = projects.filter(p => p.readiness_tier === "Tier 3");
  const filtered = search
    ? projects.filter(p => (p.org_name + p.project_name + p.sector + p.lga).toLowerCase().includes(search.toLowerCase()))
    : projects;

  const operational = isOperational(user?.role);
  const activeTabObj = TABS.find(t => t.id === tab) || TABS[0];

  // Derive SDG set active in portfolio
  const activeSdgs = Array.from(new Set(
    projects.flatMap(p => (p.sdgs_addressed || p.sdg_goals || []).map(Number))
  )).filter(n => SDG_META[n]).sort((a, b) => a - b);

  return (
    <div data-testid="ic-mode" className="fixed inset-0 flex flex-col overflow-hidden"
      style={{ background: "#060c1a", color: "#e2e8f0" }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="flex items-center justify-between flex-shrink-0 border-b border-slate-800"
        style={{ background: "#07101e", padding: "0 24px", height: 50 }}>

        {/* DeCarbonIQ SVG logo (Problem 1) */}
        <div className="flex items-center gap-3 select-none">
          <img
            src={LOGO_URL}
            alt="DeCarbonIQ"
            className="h-7 w-auto"
            style={{ filter: "brightness(0) invert(1)", maxHeight: 28 }}
          />
          <div className="w-px h-4 bg-slate-700 flex-shrink-0" />
          <span className="text-[9px] text-slate-500 uppercase tracking-widest leading-tight whitespace-nowrap">
            Lagos Climate Finance<br />Pipeline Initiative
          </span>
        </div>

        {/* Tab navigation */}
        <nav className="flex gap-0">
          {TABS.map(t => {
            const isActive = tab === t.id; const Icon = t.icon;
            return (
              <button key={t.id} data-testid={`ic-slide-${t.id}`} onClick={() => setTab(t.id)}
                style={{
                  padding: "6px 11px", borderBottom: `2px solid ${isActive ? t.accent : "transparent"}`,
                  background: isActive ? `${t.accent}10` : "transparent",
                  color: isActive ? "#fff" : "#4b5563",
                  fontSize: 11, fontWeight: isActive ? 700 : 500,
                  border: "none", borderBottom: `2px solid ${isActive ? t.accent : "transparent"}`,
                  cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 5,
                  transition: "all .12s",
                }}>
                <Icon size={10} color={isActive ? t.accent : "currentColor"} />
                {t.label}
              </button>
            );
          })}
        </nav>

        {/* Role-based actions */}
        <div className="flex items-center gap-2">
          {live && (
            <div className="flex items-center gap-1.5 rounded-full px-2 py-0.5" style={{background:"#14532d30",border:"1px solid #14532d"}}>
              <div className="w-1.5 h-1.5 rounded-full bg-green-400" style={{animation:"pulse 2s infinite"}} />
              <span className="text-[9px] font-bold text-green-400">LIVE</span>
            </div>
          )}
          <button onClick={refreshLive} data-testid="ic-refresh-live"
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-all">
            <RefreshCw size={11} />
          </button>

          {operational ? (
            /* New Project — dropdown with Register Single / Upload Bulk */
            <div ref={newProjMenuRef} style={{ position: "relative" }}>
              <button
                onClick={() => setNewProjMenu(o => !o)}
                data-testid="lcfpi-new-project-btn"
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
                style={{ background:"#1d4ed8", color:"#fff", border:"1px solid #2563eb40" }}>
                <PlusCircle size={11} /> New Project
                <ChevronDown size={10} style={{ transform: newProjMenu ? "rotate(180deg)" : "none", transition:"transform .15s" }}/>
              </button>
              {newProjMenu && (
                <div style={{
                  position:"absolute", top:"calc(100% + 4px)", right:0, width:220,
                  background:"#0d1726", border:"1px solid #1e293b", borderRadius:12,
                  boxShadow:"0 12px 28px rgba(0,0,0,.6)", zIndex:5000, overflow:"hidden",
                }}>
                  <button
                    onClick={() => { setNewProjMenu(false); openNewProject(); }}
                    data-testid="lcfpi-new-project-single-btn"
                    style={{
                      width:"100%", padding:"11px 16px", background:"transparent", border:"none",
                      borderBottom:"1px solid #1e293b", color:"#f1f5f9", fontSize:12,
                      fontWeight:600, cursor:"pointer", textAlign:"left", display:"flex",
                      alignItems:"center", gap:8, transition:"background .1s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background="#1e293b"}
                    onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                    <PlusCircle size={12} color="#60a5fa" />
                    Register Single Project
                  </button>
                  <button
                    onClick={() => { setNewProjMenu(false); setBulkUpload(true); }}
                    data-testid="lcfpi-new-project-bulk-btn"
                    style={{
                      width:"100%", padding:"11px 16px", background:"transparent", border:"none",
                      color:"#94a3b8", fontSize:12, fontWeight:500, cursor:"pointer",
                      textAlign:"left", display:"flex", alignItems:"center", gap:8, transition:"background .1s",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background="#1e293b"}
                    onMouseLeave={e => e.currentTarget.style.background="transparent"}>
                    <Upload size={12} color="#60a5fa" />
                    Upload from Excel / CSV
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <button data-testid="lcfpi-download-pipeline-btn"
                onClick={() => toast.info("Generating pipeline summary…")}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                style={{ background:"#1e293b", color:"#93c5fd", border:"1px solid #1e40af40" }}>
                <Download size={11} /> Pipeline
              </button>
              <button data-testid="lcfpi-download-summary-btn"
                onClick={() => toast.info("Generating investment summary…")}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                style={{ background:"#1e293b", color:"#93c5fd", border:"1px solid #1e40af40" }}>
                <FileText size={11} /> Memo
              </button>
            </div>
          )}
          {/* User menu — always visible */}
          <div className="w-px h-5 bg-slate-800 flex-shrink-0 mx-1" />
          <UserMenuDropdown onLock={() => { setIsLocked(true); setShowLockWarn(false); }} />
        </div>
      </header>

      {/* ── Content ─────────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-auto min-h-0" style={{ padding: tab === "map" ? "0" : "24px 28px", position:"relative" }}>

        {/* ── LIVE INTELLIGENCE ─────────────────────────────────────────── */}
        {tab === "live" && (
          <div data-testid="ic-slide-live-content">
            <div className="flex items-end justify-between mb-5">
              <div>
                <p className="text-[10px] text-blue-400 uppercase tracking-widest font-semibold mb-1">SSOT · Refreshes every 30s</p>
                <h1 className="text-4xl font-black text-white">Lagos Climate Finance Pipeline</h1>
              </div>
              {live?.timestamp && <p className="text-[10px] text-slate-600">{new Date(live.timestamp).toLocaleTimeString()}</p>}
            </div>

            {/* Row 1: 4 primary KPIs */}
            <div className="grid grid-cols-4 gap-3 mb-3">
              <KPI label="Funding Pipeline"   value={live ? fmtU(live.pipeline_total_usd) : fmtU(kpis.total_funding_pipeline_usd)}   color="#60a5fa" sub="Total assessed" />
              <KPI label="Finance Ready (T1)" value={live ? fmtU(live.finance_ready_usd) : fmtU(kpis.finance_ready_funding)}          color="#22c55e" sub="Immediate DFI" />
              <KPI label="Financing Gap"      value={live ? fmtU(live.financing_gap_usd) : "—"}                                       color="#f87171" sub="TA addressable" />
              <KPI label="Active Projects"    value={live?.total_projects ?? kpis.total_projects ?? 0}                                color="#a78bfa" sub="In pipeline" />
            </div>

            {/* Row 2: 4 pipeline status KPIs */}
            <div className="grid grid-cols-4 gap-3 mb-3">
              <KPI label="Pending Review"    value={live?.pending_review_count ?? 0}                   color="#f59e0b" />
              <KPI label="Verified"          value={live?.verification?.programme_reviewed ?? 0}        color="#1e40af" />
              <KPI label="PoA Opportunities" value={live?.poa_programmes_possible ?? 0}                color="#8b5cf6" />
              <KPI label="EOIs Active"       value={live?.eoi_active ?? 0}                             color="#0891b2" />
            </div>

            {/* Row 3: 3 climate metrics */}
            <div className="grid grid-cols-3 gap-3 mb-3">
              <KPI label="tCO₂e Monitored"        value={live ? fmtN(live.total_tco2e_monitored) : "—"}      color="#34d399" />
              <KPI label="Reduction Potential"     value={live ? fmtN(live.reduction_potential_tco2e)+" tCO₂e/yr" : "—"} color="#22c55e" />
              {/* Monetisation KPI — clickable drill-down (SSOT: same as PoA tab) */}
              <div data-testid="ic-kpi-monetisation-potential"
                className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 cursor-pointer hover:bg-slate-800 hover:border-yellow-700/40 transition-all group"
                onClick={() => setMonetModal(true)}>
                <p className="text-2xl font-black mb-0.5 text-yellow-400">
                  {live ? fmtU(live.monetisation_sample_usd_yr)+"/yr" : "—"}
                </p>
                <p className="text-xs font-semibold text-slate-300">Monetisation Potential</p>
                <p className="text-[10px] text-slate-600 group-hover:text-yellow-600 transition-colors mt-0.5">Click to drill down</p>
              </div>
            </div>

            {/* Row 4: Executive intelligence (Problem 1 — second row of metrics) */}
            <div className="grid grid-cols-6 gap-3">
              <KPI label="Avg Readiness"    value={kpis.avg_readiness_score ? kpis.avg_readiness_score.toFixed(0)+"/100" : "—"} color="#60a5fa" />
              <KPI label="Avg Climate"      value={kpis.avg_climate_score ? kpis.avg_climate_score.toFixed(0)+"/100" : "—"}    color="#34d399" />
              <KPI label="Avg Circularity"  value={kpis.avg_circularity_score ? kpis.avg_circularity_score.toFixed(0)+"/100" : "—"} color="#8b5cf6" />
              <KPI label="Evidence Avg"     value={kpis.evidence_completeness ? Math.round(kpis.evidence_completeness)+"%" : "—"} color="#d97706" />
              <KPI label="Prog. Reviewed"   value={kpis.programme_reviewed ?? 0}   color="#1e40af" />
              <KPI label="Registry Ready"   value={kpis.registry_ready ?? 0}       color="#0891b2" />
            </div>

            <p className="text-[10px] text-slate-700 mt-3 text-right">
              LCFPI SSOT · Health: {live?.portfolio_health ?? 0}/100 · GHG Protocol · DeCarbonIQ Engine V1
            </p>
          </div>
        )}

        {/* ── INVESTMENT MAP — hero experience (Problem 2, 6 fix) ──────── */}
        {tab === "map" && (
          <div data-testid="ic-slide-map-content"
            style={{ position:"absolute", inset:`50px 0 44px 0`, display:"flex", flexDirection:"column" }}>
            <div style={{ padding:"14px 28px 8px", flexShrink:0 }}>
              <h1 className="text-3xl font-black text-white">Lagos Investment Map</h1>
              <p className="text-slate-500 text-xs">{projects.length} projects · Tier pins · LGA labels · Financing gap heatmap</p>
            </div>
            {/* Map container — explicit flex-1 fill + pixel-resolved height (Problem 6 root fix) */}
            <div style={{ flex:1, padding:"0 28px 16px", minHeight:0, display:"flex", flexDirection:"column" }}>
              <div style={{ flex:1, borderRadius:14, overflow:"hidden", border:"1px solid #1e293b", minHeight:400 }}>
                <LCFPIGeoMap
                  key={`map-${mapKey}`}
                  projects={projects}
                  height="100%"
                  showLegend={true}
                  showHeatmap={true}
                />
              </div>
            </div>
          </div>
        )}

        {/* ── PORTFOLIO ─────────────────────────────────────────────────── */}
        {tab === "portfolio" && (
          <div data-testid="ic-slide-portfolio-content">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h1 className="text-4xl font-black text-white">{fmtU(kpis.total_funding_pipeline_usd)} Pipeline</h1>
                <p className="text-slate-400 mt-1">
                  <span className="text-green-400 font-bold">{fmtU(kpis.finance_ready_funding)}</span> finance-ready ·{" "}
                  <span className="text-amber-400 font-bold">{tier2.length} need TA</span> ·{" "}
                  {projects.length} total
                </p>
              </div>
              <div className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-xl px-3 py-2">
                <Search size={11} className="text-slate-500" />
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Filter projects…"
                  className="bg-transparent text-xs text-slate-300 placeholder-slate-600 outline-none w-36" />
              </div>
            </div>
            {[["Tier 1 — Finance Ready",tier1,"text-green-400","#22c55e"],["Tier 2 — Needs Technical Assistance",tier2,"text-amber-400","#f59e0b"],["Tier 3 — Early Stage",tier3,"text-red-400","#ef4444"]].map(([label,list,cls,color]) => {
              const display = search ? list.filter(p=>(p.org_name+p.project_name+p.sector+p.lga).toLowerCase().includes(search.toLowerCase())) : list;
              if (!display.length) return null;
              return (
                <div key={label} className="mb-5">
                  <div className="flex items-center gap-2 mb-2.5">
                    <div className="w-2 h-2 rounded-full" style={{background:color}} />
                    <p className={`text-xs font-bold uppercase tracking-wider ${cls}`}>{label}</p>
                    <span className="text-xs text-slate-600">({list.length})</span>
                  </div>
                  <div className="space-y-1.5">{display.slice(0,6).map(p=><ProjectCard key={p.project_id} p={p} onOpen={openProject}/>)}</div>
                </div>
              );
            })}
            {projects.length === 0 && (
              <div className="text-center py-16 text-slate-600">
                <GitMerge size={32} className="mx-auto mb-3 opacity-30" />
                <p className="text-sm">No projects in pipeline</p>
                {operational && <button onClick={openNewProject} className="mt-3 text-xs text-blue-400 hover:text-blue-300">+ Add first project</button>}
              </div>
            )}
          </div>
        )}

        {/* ── OPPORTUNITIES ─────────────────────────────────────────────── */}
        {tab === "opportunity" && (
          <div data-testid="ic-slide-opportunity-content">
            <div className="mb-5">
              <h1 className="text-4xl font-black text-white mb-1">Finance-Ready Pipeline</h1>
              <p className="text-slate-500 text-xs">Tier 1 · Assessed · Verified · DFI-ready</p>
            </div>
            <div className="space-y-2.5">
              {opps.slice(0,8).map((opp,i) => {
                const tc = TIER_COLOR[opp.readiness_tier] || "#3b82f6";
                return (
                  <div key={opp.project_id} data-testid={`lcfpi-opp-card-${opp.project_id}`}
                    className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 flex items-center gap-5"
                    style={{ borderLeft:`3px solid ${tc}` }}>
                    <span className="text-xs font-black text-slate-500 w-4 text-center flex-shrink-0">{i+1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-bold text-white truncate">{opp.org_name}</p>
                        <TierBadge tier={opp.readiness_tier} />
                      </div>
                      <p className="text-xs text-slate-500">{opp.sector?.replace(/_/g," ")} · {opp.lga}</p>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <div className="text-right"><p className="text-base font-black text-blue-400">{fmtU(opp.funding_need_usd)}</p><p className="text-[10px] text-slate-500">{opp.indicative_irr||"—"} IRR</p></div>
                      <div className="text-center"><p className="text-sm font-black" style={{color:tc}}>{opp.readiness_score?.toFixed(0)??"—"}</p><p className="text-[10px] text-slate-500">Readiness</p></div>
                      <div className="text-center"><p className="text-sm font-black text-green-400">{opp.climate_score?.toFixed(0)??"—"}</p><p className="text-[10px] text-slate-500">Climate</p></div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* DFI Match button */}
                      <button onClick={() => setDfiProject(opp)}
                        data-testid={`ic-match-dfi-${opp.project_id}`}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg"
                        style={{background:"#1e293b",border:"1px solid #334155",color:"#a78bfa"}}>
                        <Target size={10}/> DFI Match
                      </button>
                      <button onClick={()=>downloadMemo(opp.project_id)} disabled={downloading===opp.project_id}
                        data-testid={`ic-download-memo-${opp.project_id}`}
                        className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg disabled:opacity-40"
                        style={{background:"#1e40af20",border:"1px solid #1e40af40",color:"#93c5fd"}}>
                        {downloading===opp.project_id ? <Loader size={10} className="animate-spin"/> : <Download size={10}/>} Memo
                      </button>
                    </div>
                  </div>
                );
              })}
              {opps.length === 0 && <div className="text-center py-14 text-slate-600"><DollarSign size={28} className="mx-auto mb-2 opacity-30"/><p className="text-sm">No Tier 1 opportunities yet</p></div>}
            </div>
          </div>
        )}

        {/* ── PROGRAMME OF ACTIVITIES (Problem 7) ───────────────────────── */}
        {tab === "poa" && (
          <div data-testid="ic-slide-poa-content">
            <div className="flex items-end justify-between mb-5">
              <div>
                <h1 className="text-4xl font-black text-white mb-1">Programme of Activities</h1>
                <p className="text-slate-500 text-xs">Aggregate SME pipeline into bankable climate programmes</p>
              </div>
              {operational && (
                <button onClick={()=>setPoaModal(true)} data-testid="lcfpi-create-poa-btn"
                  className="flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-xl"
                  style={{background:"#1d4ed8",color:"#fff",border:"1px solid #2563eb40"}}>
                  <PlusCircle size={12}/> Create PoA
                </button>
              )}
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-4 gap-3 mb-5">
              <KPI label="PoA Programmes"    value={poa?.total_programmes_possible || "—"}    color="#a78bfa" />
              <KPI label="Eligible Projects" value={poa?.total_eligible_projects || "—"}       color="#22c55e" />
              <KPI label="Revenue Potential" value={fmtU(poa?.total_revenue_potential_usd)+"/yr"} color="#fbbf24" />
              <KPI label="Validation Savings" value={fmtU(poa?.validation_cost_savings_usd)}  color="#34d399" />
            </div>

            {/* Existing PoA programmes */}
            {poaProgs.length > 0 && (
              <div className="mb-5">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Active Programmes</p>
                <div className="grid grid-cols-2 gap-3">
                  {poaProgs.map(p => (
                    <div key={p.id||p.name} data-testid={`lcfpi-poa-card-${p.id}`}
                      className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                      <div className="flex items-start justify-between mb-2">
                        <p className="text-sm font-bold text-white">{p.name}</p>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                          style={{background:"#14532d30",color:"#22c55e",border:"1px solid #22c55e30"}}>
                          {p.status||"Active"}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div><p className="text-sm font-black text-blue-400">{p.project_count||0}</p><p className="text-[10px] text-slate-500">Projects</p></div>
                        <div><p className="text-sm font-black text-amber-400">{fmtU(p.revenue_potential_usd)}</p><p className="text-[10px] text-slate-500">Revenue/yr</p></div>
                        <div><p className="text-sm font-black text-green-400">{fmtU(p.validation_savings_usd)}</p><p className="text-[10px] text-slate-500">TA savings</p></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rationale cards */}
            <div className="grid grid-cols-2 gap-3">
              {[
                ["Aggregation → Bankability","SMEs sub-threshold individually. PoA creates bankable scale with continuous credit issuance."],
                ["45% Cost Reduction","Shared VVB validation across all CPAs reduces per-project costs by up to 45%."],
                ["Registry-Ready","Verra VCS · Gold Standard · PCR PoA with defined methodology and shared monitoring."],
                ["Single Investment Document","One document covering all CPAs. Suitable for DFI co-finance and blended structures."],
              ].map(([t,d]) => (
                <div key={t} className="bg-slate-800/40 border border-slate-700/40 rounded-xl p-4">
                  <p className="text-sm font-bold text-white mb-1">{t}</p>
                  <p className="text-xs text-slate-400 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>

            {/* Carbon Monetisation — interactive workspace (replaces static 4 KPIs) */}
            <PoACarbonWorkspace poa={poa} projects={projects} />
            <p className="text-[10px] text-slate-700 mt-3 text-center">Indicative only · Subject to methodology registration · NOT bankable without VVB validation</p>
          </div>
        )}

        {/* ── IMPACT TAB (Climate + Circularity + SDG only) ─────────────── */}
        {tab === "climate" && (
          <div data-testid="ic-slide-climate-content">
            <div className="mb-4">
              <h1 className="text-4xl font-black text-white mb-1">Impact</h1>
              <p className="text-slate-500 text-xs">Climate · Circularity · SDGs</p>
            </div>

            {/* A. Climate Impact */}
            <p className="text-xs font-bold text-blue-400 uppercase tracking-wider mb-2">Climate Impact</p>
            <div className="grid grid-cols-4 gap-3 mb-5">
              <KPI label="Baseline Emissions"  value={fmtN(kpis.total_emissions_tco2e||0)+" tCO₂e"}             color="#f87171" />
              <KPI label="Avoided Emissions"   value={fmtN(Math.round((kpis.total_emissions_tco2e||0)*0.35))+" tCO₂e/yr"} color="#34d399" />
              <KPI label="Reduction Potential" value={fmtN(live?.reduction_potential_tco2e||kpis.reduction_potential_tco2e||0)+" tCO₂e/yr"} color="#22c55e" />
              <KPI label="tCO₂e Monitored"    value={fmtN(live?.total_tco2e_monitored||kpis.total_tco2e_monitored||0)} color="#60a5fa" />
            </div>

            {/* B. Circularity Impact */}
            <p className="text-xs font-bold text-purple-400 uppercase tracking-wider mb-2">Circularity Impact</p>
            <div className="grid grid-cols-4 gap-3 mb-5">
              <KPI label="Waste Diverted"       value={fmtN(kpis.waste_diverted_tonnes||0)+" t/yr"}             color="#8b5cf6" />
              <KPI label="Materials Recovered"  value={fmtN(kpis.materials_recovered_tonnes||0)+" t/yr"}        color="#7c3aed" />
              <KPI label="Circularity Score"    value={kpis.avg_circularity_score ? kpis.avg_circularity_score.toFixed(0)+"/100" : "—"} color="#a78bfa" />
              <KPI label="Recycled Input"       value={kpis.recycled_input_pct ? kpis.recycled_input_pct.toFixed(0)+"%" : "—"} color="#6d28d9" />
            </div>

            {/* C. SDG Impact */}
            <p className="text-xs font-bold text-cyan-400 uppercase tracking-wider mb-2">SDG Impact</p>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <KPI label="SDG Alignment Score" value={kpis.avg_sdg_score ? kpis.avg_sdg_score.toFixed(0)+"/100" : "—"} color="#0891b2" />
              <KPI label="SDGs Addressed"      value={kpis.sdg_count||activeSdgs.length||"—"}                   color="#1e40af" />
              <KPI label="SDG-Linked Projects" value={kpis.sdg_aligned_count||"—"}                              color="#3b82f6" />
            </div>

            {/* D. SDG Tiles */}
            <div className="flex flex-wrap gap-2 mb-2">
              {(activeSdgs.length > 0 ? activeSdgs : [7,8,9,11,12,13]).map(num => {
                const s = SDG_META[num]; if (!s) return null;
                const isActive = activeSdgs.includes(num);
                return (
                  <div key={num} data-testid={`lcfpi-sdg-${num}`}
                    className="flex flex-col items-center justify-center rounded-xl text-center"
                    style={{ width:72, height:72, opacity:isActive?1:0.4, background:`${s.color}22`, border:`2px solid ${s.color}60` }}>
                    <p className="text-lg font-black" style={{color:s.color}}>{num}</p>
                    <p className="text-[8px] font-bold text-slate-400 mt-0.5 leading-tight px-1">{s.label}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-slate-700 mt-2 text-right">GHG Protocol · DeCarbonIQ Engine V1.0</p>
          </div>
        )}

        {/* ── GOVERNANCE (Problem 4) ─────────────────────────────────────── */}
        {tab === "governance" && (
          <div data-testid="ic-slide-governance-content">
            <div className="mb-5">
              <h1 className="text-4xl font-black text-white mb-1">Governance</h1>
              <p className="text-slate-500 text-xs">Credibility · Verification · Quality · Readiness</p>
            </div>
            <GovernancePanel projects={projects} kpis={kpis} />
          </div>
        )}
      </main>

      {/* ── Bottom navigation ────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-8 py-2 border-t border-slate-800/50 flex-shrink-0"
        style={{ background: "#07101e" }}>
        <button onClick={() => { const i = TABS.findIndex(t=>t.id===tab); if(i>0) setTab(TABS[i-1].id); }}
          disabled={tab===TABS[0].id}
          className="text-xs text-slate-600 hover:text-slate-400 disabled:opacity-20 flex items-center gap-1">
          <ChevronLeft size={12}/> Prev
        </button>
        <div className="flex gap-1.5">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ background: tab===t.id ? activeTabObj.accent : "#1e293b", width:6, height:6, borderRadius:"50%" }}
              className="transition-all" />
          ))}
        </div>
        <button onClick={() => { const i = TABS.findIndex(t=>t.id===tab); if(i<TABS.length-1) setTab(TABS[i+1].id); }}
          disabled={tab===TABS[TABS.length-1].id}
          className="text-xs text-slate-600 hover:text-slate-400 disabled:opacity-20 flex items-center gap-1">
          Next <ChevronRight size={12}/>
        </button>
      </div>

      {/* ── Create PoA Modal ─────────────────────────────────────────────── */}
      {poaModal && (
        <CreatePoAModal
          projects={projects}
          onClose={() => setPoaModal(false)}
          onCreated={loadPoaProgs}
        />
      )}

      {/* ── New Project Modal (in-page dark IC experience) ─────────────── */}
      {newProjModal && (
        <NewProjectModal
          onClose={() => setNewProjModal(false)}
          onSaved={(newP) => {
            setProjects(prev => [newP, ...prev]);
            setNewProjModal(false);
          }}
        />
      )}

      {/* ── Project Workspace Modal (view/edit existing project) ──────── */}
      {editProjId && (
        <ProjectWorkspaceModal
          projectId={editProjId}
          onClose={() => setEditProjId(null)}
        />
      )}

      {/* ── Monetisation Drill-down Modal ───────────────────────────────── */}
      {monetModal && (
        <MonetisationDrilldownModal onClose={() => setMonetModal(false)} />
      )}

      {/* ── DFI Matching Workspace ──────────────────────────────────────── */}
      {dfiProject && (
        <DFIMatchingModal
          project={dfiProject}
          onClose={() => setDfiProject(null)}
        />
      )}

      {/* ── Bulk Upload Wizard ──────────────────────────────────────────── */}
      {bulkUpload && (
        <BulkUploadWizard
          onClose={() => setBulkUpload(false)}
          onImported={() => {
            setBulkUpload(false);
            // Reload data after import
            Promise.all([
              client.get("/lcfpi/projects"),
              client.get("/lcfpi/live-intelligence"),
              client.get("/lcfpi/investor/portfolio"),
              client.get("/lcfpi/poa/economics"),
            ]).then(([proj, liveR, inv, poaR]) => {
              setProjects(proj.data.projects || []);
              setLive(liveR.data);
              setInvestor(inv.data);
              setPoa(poaR.data);
            }).catch(() => {});
            toast.success("Pipeline refreshed with imported projects");
          }}
        />
      )}

      {/* ── Inactivity Warning Banner ───────────────────────────────────── */}
      {showLockWarn && !isLocked && (
        <InactivityWarning
          onContinue={() => setShowLockWarn(false)}
          onLock={() => {
            setShowLockWarn(false);
            setIsLocked(true);
            client.post("/lcfpi/session-events", { action: "manual_lock", metadata: { triggered_by: "warning_banner" } }).catch(() => {});
          }}
        />
      )}

      {/* ── Lock Screen ─────────────────────────────────────────────────── */}
      {isLocked && (
        <LockScreen
          onUnlocked={() => setIsLocked(false)}
        />
      )}
    </div>
  );
}
